import { useState } from 'react';
import { admissionRequirements, fees, programInfo } from '@/mocks/programData';

export default function Admission() {
  const [expanded, setExpanded] = useState<number | null>(1);

  return (
    <section id="admissao" className="py-20 md:py-28 bg-warm-50">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            <div>
              <span className="text-accent text-sm font-semibold uppercase tracking-wider">Processo Seletivo</span>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-primary mt-3 mb-4">
                Requisitos de Admissão
              </h2>
              <p className="text-gray-600 leading-relaxed mb-8">
                A selecção dos candidatos é realizada mediante apreciação do dossier da candidatura
                e classificação em prova de acesso, através de entrevista.
              </p>

              <div className="space-y-3">
                {admissionRequirements.map((req) => (
                  <div
                    key={req.id}
                    className={`bg-white rounded-xl border transition-all cursor-pointer ${
                      expanded === req.id ? 'border-accent/40 shadow-md' : 'border-gray-200'
                    }`}
                  >
                    <button
                      onClick={() => setExpanded(expanded === req.id ? null : req.id)}
                      className="w-full flex items-center gap-4 p-5 text-left"
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                        expanded === req.id ? 'bg-accent text-white' : 'bg-primary/10 text-primary'
                      }`}>
                        <i className={`${req.icon} text-lg`} />
                      </div>
                      <span className="font-semibold text-primary text-sm flex-1">{req.title}</span>
                      <i className={`ri-arrow-down-s-line text-gray-400 transition-transform ${expanded === req.id ? 'rotate-180' : ''}`} />
                    </button>
                    {expanded === req.id && (
                      <div className="px-5 pb-5 pl-[4.5rem]">
                        <p className="text-gray-600 text-sm leading-relaxed">{req.description}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-8">
                <h3 className="text-xl font-bold text-primary mb-6 flex items-center gap-3">
                  <i className="ri-money-cny-circle-line text-accent" />
                  Emolumentos e Propinas
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-warm-50 rounded-xl">
                    <div>
                      <p className="font-semibold text-primary text-sm">{fees.inscription.label}</p>
                      <p className="text-gray-500 text-xs">Pagamento único</p>
                    </div>
                    <p className="text-lg font-bold text-accent">
                      {fees.inscription.amount} <span className="text-sm font-normal text-gray-500">{fees.currency}</span>
                    </p>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-warm-50 rounded-xl">
                    <div>
                      <p className="font-semibold text-primary text-sm">{fees.enrollment.label}</p>
                      <p className="text-gray-500 text-xs">Pagamento único</p>
                    </div>
                    <p className="text-lg font-bold text-accent">
                      {fees.enrollment.amount} <span className="text-sm font-normal text-gray-500">{fees.currency}</span>
                    </p>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-primary/5 rounded-xl border border-primary/10">
                    <div>
                      <p className="font-semibold text-primary text-sm">{fees.monthly.label}</p>
                      <p className="text-gray-500 text-xs">Pagamento mensal durante o curso</p>
                    </div>
                    <p className="text-lg font-bold text-accent">
                      {fees.monthly.amount} <span className="text-sm font-normal text-gray-500">{fees.currency}</span>
                    </p>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-4">
                  Pagamento via RUPE na Faculdade de Medicina da UAN. Solicitar nota de liquidação por e-mail.
                </p>
              </div>

              <div className="bg-primary rounded-2xl p-8 text-white">
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <i className="ri-information-line text-accent" />
                  Critérios de Preferência
                </h3>
                <ul className="space-y-3 text-sm text-white/80">
                  <li className="flex items-start gap-3">
                    <span className="w-5 h-5 rounded-full bg-accent/30 flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-bold text-accent">1</span>
                    Capacidade em informática na óptica do utilizador
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-5 h-5 rounded-full bg-accent/30 flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-bold text-accent">2</span>
                    Domínio elementar da língua inglesa
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-5 h-5 rounded-full bg-accent/30 flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-bold text-accent">3</span>
                    Pertencer ao quadro docente universitário ou investigador
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-5 h-5 rounded-full bg-accent/30 flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-bold text-accent">4</span>
                    Género feminino (critério de equidade)
                  </li>
                </ul>
              </div>

              <div className="bg-accent/10 rounded-2xl p-6 border border-accent/20">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-primary text-sm mb-1">Contacto para Candidaturas</p>
                    <p className="text-gray-600 text-sm">{programInfo.contactEmail}</p>
                    <p className="text-gray-600 text-sm">{programInfo.contactPhone}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}